import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, ChefHat, Sparkles, WifiOff, Users, LayoutGrid, ArrowUpRight } from "lucide-react";
import { scaleReveal, fadeUp } from "@/lib/anim";

const IMG = {
  reception:
    "https://images.unsplash.com/photo-1553369728-15ec6971afaf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  kitchen:
    "https://images.unsplash.com/photo-1769955817432-641929f613f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  phone:
    "https://images.unsplash.com/photo-1742134833327-76db52aa3de8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  homestay:
    "https://images.unsplash.com/photo-1770785048349-33aa75e23c3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

const Card = ({ children, className = "", testid }) => (
  <motion.div
    variants={scaleReveal}
    data-testid={testid}
    className={`group relative rounded-3xl border border-black/5 bg-white overflow-hidden shadow-[0_1px_2px_rgba(20,20,20,0.04)] hover:shadow-[0_20px_50px_rgba(20,20,20,0.10)] transition-shadow duration-500 ${className}`}
  >
    {children}
  </motion.div>
);

const Pill = ({ icon: Icon, label }) => (
  <div className="inline-flex items-center gap-2 rounded-full bg-brand-soft text-brand-dark px-3 py-1.5 text-xs font-semibold">
    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
    {label}
  </div>
);

export const Features = () => {
  return (
    <section id="features" className="py-24 md:py-32 bg-paper-muted scroll-mt-24" data-testid="features">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="overline text-brand"
          >
            One app, everything covered
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-5 font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-ink leading-tight"
          >
            Built for the way independent venues actually run.
          </motion.h2>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09 } } }}
          className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[minmax(0,1fr)]"
        >
          {/* Unified Management — span 8, image */}
          <Card testid="feature-unified" className="md:col-span-8 min-h-[360px] flex flex-col">
            <div className="relative flex-1 overflow-hidden">
              <img src={IMG.reception} alt="Front desk check-in" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <Pill icon={LayoutGrid} label="Unified Management" />
                <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl text-white tracking-tight">
                  Rooms, tables, orders & invoices — one workspace
                </h3>
                <p className="mt-3 max-w-md text-white/75">
                  Replace chaotic threads and scattered forms with a single streamlined
                  place to run the whole property.
                </p>
              </div>
            </div>
          </Card>

          {/* Interactive Calendar — span 4 */}
          <Card testid="feature-calendar" className="md:col-span-4 min-h-[360px] p-8 flex flex-col justify-between">
            <Pill icon={CalendarDays} label="Interactive Calendar" />
            <div>
              <div className="grid grid-cols-7 gap-1.5 mb-6" aria-hidden="true">
                {Array.from({ length: 28 }).map((_, i) => (
                  <span
                    key={i}
                    className={`h-5 rounded-md ${[3, 4, 5, 11, 12, 18, 19, 20].includes(i) ? "bg-brand" : "bg-black/[0.06]"}`}
                  />
                ))}
              </div>
              <h3 className="font-heading font-bold text-2xl text-ink tracking-tight">A visual grid for every arrival</h3>
              <p className="mt-2 text-ink-soft">Track check-ins, block dates and see real-time availability at a glance.</p>
            </div>
          </Card>

          {/* Kitchen POS — span 4, image spotlight */}
          <Card testid="feature-pos" className="md:col-span-4 min-h-[360px] flex flex-col">
            <div className="relative flex-1 overflow-hidden bg-ink">
              <img src={IMG.kitchen} alt="Chef under kitchen spotlight" className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <Pill icon={ChefHat} label="Kitchen POS & KDS" />
                <h3 className="mt-4 font-heading font-bold text-2xl text-white tracking-tight">Orders straight to the kitchen</h3>
                <p className="mt-2 text-white/75">Table tabs, split bills, room charges and a live kitchen display — built in.</p>
              </div>
            </div>
          </Card>

          {/* AI Smart Booking — span 8, image */}
          <Card testid="feature-ai" className="md:col-span-8 min-h-[360px] grid md:grid-cols-2">
            <div className="p-8 md:p-10 flex flex-col justify-center order-2 md:order-1">
              <Pill icon={Sparkles} label="AI Smart Booking" />
              <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl text-ink tracking-tight">
                Snap a message, we fill the form
              </h3>
              <p className="mt-3 text-ink-soft">
                Upload a screenshot or photo of a booking message and let AI read the details
                and pre-fill the booking automatically.
              </p>
            </div>
            <div className="relative overflow-hidden order-1 md:order-2 min-h-[220px]">
              <img src={IMG.phone} alt="Guest using the app on a phone" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </Card>

          {/* Offline — full width, dark */}
          <Card testid="feature-offline" className="md:col-span-7 min-h-[220px] bg-ink p-8 md:p-10 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white px-3 py-1.5 text-xs font-semibold w-fit">
              <WifiOff className="w-3.5 h-3.5" aria-hidden="true" /> 100% Offline Capability
            </div>
            <h3 className="mt-4 font-heading font-bold text-2xl md:text-3xl text-white tracking-tight">
              Keeps working when the network doesn't
            </h3>
            <p className="mt-3 max-w-lg text-white/70">
              Operate fully offline in areas with spotty coverage. Everything auto-syncs the
              moment your connection is restored.
            </p>
          </Card>

          {/* Customer & Business Tools — span 5, image */}
          <Card testid="feature-tools" className="md:col-span-5 min-h-[220px] flex flex-col">
            <div className="relative flex-1 overflow-hidden">
              <img src={IMG.homestay} alt="Cozy homestay terrace" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <Pill icon={Users} label="Guest & Business Tools" />
                <h3 className="mt-4 font-heading font-bold text-2xl text-white tracking-tight">Guests, staff & finances in one place</h3>
                <p className="mt-2 text-white/75">Identity proofs, staff permissions and records — centralized and mobile-first.</p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* For hosts — free forever, with optional paid plans */}
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-5 rounded-3xl border border-black/10 bg-paper-muted/60 px-7 py-6">
          <p className="text-ink-soft max-w-xl leading-relaxed">
            <span className="font-semibold text-ink">Free forever for verified hosts.</span>{" "}
            StayFlow Homestay adds unlimited offline bookings, POS, KDS and AI tools from ₹833 a month.
          </p>
          <Link
            to="/pro"
            data-testid="features-pro-link"
            className="group shrink-0 inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-sm font-medium pl-5 pr-4 h-11 transition-transform duration-200 hover:-translate-y-0.5"
          >
            Plans &amp; pricing
            <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Features;
